import { useState, useEffect } from 'react';
import { auth, db } from '../../Services/firebase-config';
import { doc, getDoc } from 'firebase/firestore';

export const useHackathonProgress = (event, currentTeam, userApplication, evaluationData, applicationData, mentorResources) => {
    const [progressData, setProgressData] = useState([
        { title: "Resources", percentage: 0, status: "pending" },
        { title: "Application", percentage: 0, status: "pending" },
        { title: "Evaluation", percentage: 0, status: "pending" },
    ]);
    const [showApplication, setShowApplication] = useState(false);
    const [applicationWindowOpen, setApplicationWindowOpen] = useState(false);
    const [applicationWindowStart, setApplicationWindowStart] = useState(null);
    const [applicationWindowEnd, setApplicationWindowEnd] = useState(null);

    // Calculate application progress based on form completion
    const calculateApplicationProgress = (appData, teamData, submittedApplication) => {
        // Use current applicationData state if no data passed or if passed data is null
        const dataToUse = appData || applicationData;

        // Use passed team data or current team state
        const teamToUse = teamData || currentTeam;

        // Use passed application or current application state
        const applicationToUse = submittedApplication || userApplication;

        // If application is submitted, it's 100%
        if (applicationToUse) {
            return 100;
        }

        // Base progress for being in a team
        let progress = teamToUse ? 10 : 0;

        // If no data to work with, return base progress
        if (!dataToUse) {
            return progress;
        }

        // Calculate progress based on filled fields
        const requiredFields = [
            "teamName",
            "teamSize",
            "teamLeaderName",
            "teamLeaderEmail",
            "teamLeaderPhone",
            "category",
            "availability",
            "timeCommitment",
            "pitchDeckFile",
            "youtubePitchDeck",
            "projectDemoLink",
        ];

        const optionalFields = ["socialMediaPosts", "referralSource"];
        const allFields = [...requiredFields, ...optionalFields];

        const answeredFields = allFields.filter((field) => {
            const value = dataToUse[field];
            if (field === "socialMediaPosts") {
                return (
                    value &&
                    Array.isArray(value) &&
                    value.some((post) => post.trim() !== "")
                );
            }
            return value && value !== "" && value !== false && value !== null;
        }).length;

        // Calculate field completion percentage (90% max, leaving 10% for team membership)
        const fieldCompletionPercentage = Math.round(
            (answeredFields / allFields.length) * 90
        );

        return Math.min(progress + fieldCompletionPercentage, 100);
    };

    // Calculate resources progress
    const calculateResourcesProgress = () => {
        let resourcesPercentage = 0;

        if (event?.eventType === "hackathon" || event?.eventType === "grant") {
            if (mentorResources?.length > 0 && auth.currentUser) {
                const totalResources = mentorResources.length;
                const completedResources = mentorResources.filter(resource =>
                    resource.readBy &&
                    Array.isArray(resource.readBy) &&
                    resource.readBy.includes(auth.currentUser.uid)
                ).length;

                if (totalResources > 0) {
                    resourcesPercentage = Math.round(
                        (completedResources / totalResources) * 100
                    );
                }
            }
        }

        return resourcesPercentage;
    };

    // Main progress calculation
    useEffect(() => {
        const newProgress = [];

        // 1. Resources Progress (only for hackathon/grant events)
        if (event?.eventType === "hackathon" || event?.eventType === "grant") {
            const resourcesPercentage = calculateResourcesProgress();

            // If application window is open, allow application to show regardless of resources completion.
            // Otherwise, fall back to previous behavior where resources must be completed.
            setShowApplication(applicationWindowOpen ? true : false);

            newProgress.push({
                title: "Resources",
                percentage: resourcesPercentage,
                status:
                    resourcesPercentage === 100
                        ? "completed"
                        : resourcesPercentage > 0
                            ? "in-progress"
                            : "pending",
            });
        }

        // 2. Application Progress
        const applicationPercentage = calculateApplicationProgress(
            applicationData,
            currentTeam,
            userApplication
        );

        const applicationStatus = userApplication
            ? "completed"
            : applicationPercentage > 10
                ? "in-progress"
                : "pending";

        newProgress.push({
            title: "Application",
            percentage: applicationPercentage,
            status: applicationStatus,
        });

        // 3. Evaluation Progress
        let evaluationPercentage = 0;
        let evaluationStatus = "pending";

        if (userApplication) {
            // Base 10% for having submitted application
            evaluationPercentage = 10;
            evaluationStatus = "in-progress";

            if (userApplication.isEvaluated) {
                evaluationPercentage = 100;
                evaluationStatus = "completed";
            }
        }

        newProgress.push({
            title: "Evaluation",
            percentage: evaluationPercentage,
            status: evaluationStatus,
        });

        setProgressData(newProgress);
    }, [event, currentTeam, userApplication, evaluationData, applicationData, mentorResources, applicationWindowOpen]);

    // Fetch application window (startDate/endDate) from Firestore configs/applicationDate
    useEffect(() => {
        let mounted = true;

        const fetchApplicationWindow = async () => {
            try {
                const docRef = doc(db, 'configs', 'applicationDate');
                const snap = await getDoc(docRef);

                if (!mounted) return;

                if (snap.exists()) {
                    const data = snap.data();
                    const { startDate, endDate } = data || {};

                    // Expect timestamps or ISO strings. Try to handle both.
                    const now = new Date();

                    let start = startDate ? (startDate.toDate ? startDate.toDate() : new Date(startDate)) : null;
                    let end = endDate ? (endDate.toDate ? endDate.toDate() : new Date(endDate)) : null;

                    if (start && end) {
                        // Normalize times (inclusive)
                        start = new Date(start);
                        end = new Date(end);

                        // If now is within window (inclusive), open applications
                        setApplicationWindowStart(start);
                        setApplicationWindowEnd(end);
                        setApplicationWindowOpen(now >= start && now <= end);
                    } else {
                        // If dates not configured, default to false
                        setApplicationWindowStart(null);
                        setApplicationWindowEnd(null);
                        setApplicationWindowOpen(false);
                    }
                } else {
                    setApplicationWindowStart(null);
                    setApplicationWindowEnd(null);
                    setApplicationWindowOpen(false);
                }
            } catch (err) {
                console.error('Failed to load application window from configs/applicationDate', err);
                setApplicationWindowStart(null);
                setApplicationWindowEnd(null);
                setApplicationWindowOpen(false);
            }
        };

        fetchApplicationWindow();

        return () => { mounted = false; };
    }, []);

    // Helper function to get the status based on current view
    const getProgressStatus = (itemTitle) => {
        // Return the original status for completed items
        if (itemTitle === "Application" && userApplication) {
            return "completed";
        }
        if (itemTitle === "Evaluation" && evaluationData) {
            return "completed";
        }
        if (itemTitle === "Resources") {
            return "completed"; // Assuming resources are always available
        }

        return "pending";
    };

    // Recalculate progress manually (for form updates)
    const recalculateProgress = (newApplicationData) => {
        const newProgress = [...progressData];

        // Find and update application progress
        const appProgressIndex = newProgress.findIndex(item => item.title === "Application");
        if (appProgressIndex !== -1) {
            const newPercentage = calculateApplicationProgress(
                newApplicationData,
                currentTeam,
                userApplication
            );

            newProgress[appProgressIndex] = {
                ...newProgress[appProgressIndex],
                percentage: newPercentage,
                status: userApplication
                    ? "completed"
                    : newPercentage > 10
                        ? "in-progress"
                        : "pending",
            };
        }

        setProgressData(newProgress);
    };

    // Expose final showApplication which is true if user already applied, or if window/resources allow
    const finalShowApplication = userApplication ? true : showApplication;

    return {
        progressData,
        showApplication: finalShowApplication,
        applicationWindowOpen,
        applicationWindowStart,
        applicationWindowEnd,
        getProgressStatus,
        recalculateProgress,
    };
};